/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import {
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { useRef } from 'react';
import { type VerticalId } from '@/types/task';

/**
 * Drag sensors.
 *
 * The vanilla board uses HTML5 `draggable` + `ondragstart`, which **does not fire
 * on touch at all** — so today a phone or a tablet cannot move a card between
 * columns. That is the reason for @dnd-kit, not a preference.
 *
 *  - **Mouse: an 8px distance constraint.** A card is a link to its own task, and
 *    without this every click would begin a drag and swallow the navigation.
 *  - **Touch: a 220ms long press.** A distance constraint on touch would capture
 *    every vertical swipe and the board would stop scrolling — which is worse
 *    than not being able to drag. Press and hold to pick a card up; swipe to
 *    scroll as usual. The tolerance lets a finger wobble during the press.
 */
export function useBoardSensors() {
  return useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );
}

/**
 * A drag ends with a click on the card underneath it, because the card is a link.
 * dnd-kit stops the drag but not that click, so a dropped card would also open
 * its own task.
 *
 * The guard is a timestamp rather than a boolean: `isDragging` is already false
 * by the time the click arrives, so there is nothing left to ask.
 */
export function useClickAfterDragGuard() {
  // -Infinity, not 0. `performance.now()` starts near zero on a fresh page load,
  // so a 0 seed swallowed every click in the first 250ms after load — before any
  // drag had happened at all. It only showed up as a card that would not open if
  // you clicked it quickly enough.
  const draggedAt = useRef(-Infinity);
  return {
    markDragged: () => {
      draggedAt.current = performance.now();
    },
    /** True when this click is the tail of a drag and should not navigate. */
    shouldSwallowClick: () => performance.now() - draggedAt.current < 250,
  };
}

/** The column a card was dropped on, or null when it was dropped nowhere useful. */
export function droppedVertical(event: DragEndEvent): VerticalId | null {
  const over = event.over?.id;
  if (typeof over !== 'string') return null;
  // Any droppable id is a column id: the only droppables the board registers
  // are its own columns, and which columns those are is the circle's business,
  // not this file's. It used to check membership of a five-item constant, which
  // would now silently reject every column a circle invented — a drag that
  // looks like it worked and quietly does nothing.
  return over;
}
